import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "wtb_install_dismissed";

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

export const InstallPrompt = () => {
  const { pathname } = useLocation();
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY) === "1" || isStandalone()) return undefined;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS has no beforeinstallprompt — show a short instruction after a beat
    let t;
    if (isIOS()) {
      t = setTimeout(() => {
        setIosHint(true);
        setShow(true);
      }, 2500);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      if (t) clearTimeout(t);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice.catch(() => {});
    setDeferred(null);
    dismiss();
  };

  if (!show || !pathname.startsWith("/account")) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed inset-x-3 bottom-[calc(4.75rem+env(safe-area-inset-bottom))] z-50 mx-auto max-w-md rounded-3xl border border-ink/10 bg-white p-4 shadow-[0_24px_60px_-28px_rgba(15,23,42,0.45)] md:bottom-4"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-bark-soft text-bark">
          <Download className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-sm font-semibold text-ink">Add WhatTheBark to your home screen</p>
          {iosHint ? (
            <p className="mt-0.5 flex items-center gap-1 text-xs leading-relaxed text-slate-500">
              Tap <Share className="inline h-3.5 w-3.5 text-bark" aria-hidden="true" /> Share, then "Add to Home Screen".
            </p>
          ) : (
            <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
              Open bookings and your pups' profiles instantly — like a real app.
            </p>
          )}
          {!iosHint && (
            <button
              type="button"
              data-testid="install-accept"
              onClick={install}
              className="mt-2.5 inline-flex items-center gap-2 rounded-full bg-bark px-4 py-2 text-xs font-semibold text-white transition-colors duration-200 hover:bg-bark-dark"
            >
              <Download className="h-3.5 w-3.5" aria-hidden="true" />
              Install app
            </button>
          )}
        </div>
        <button
          type="button"
          data-testid="install-dismiss"
          onClick={dismiss}
          aria-label="Dismiss"
          className="rounded-full p-1.5 text-slate-400 transition-colors duration-200 hover:bg-cream-dark hover:text-navy"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
